import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, LogIn, FolderKanban, ClipboardCheck, BarChart3,
  CalendarDays, ClipboardList, Truck, Flame, FileCheck2, Sparkles,
  ShieldCheck, QrCode, ExternalLink, Smartphone, Users as UsersIcon,
  Brain, Globe2, Maximize2,
} from 'lucide-react';
import { useLang } from '../../contexts/LanguageContext';
import {
  BBMI_APP_DOMAIN, BBMI_PLAY_STORE_URL, BBMI_SCREENSHOTS,
  DEMO_DATA_NOTE_EN, DEMO_DATA_NOTE_NL,
  FLOWCREW_APP_DOMAIN, FLOWCREW_PLAY_STORE_URL, FLOWCREW_PLAY_STORE_LIVE,
  FLOWCREW_ACCENT, FLOWCREW_SCREENSHOTS, KANRIVA_SCREENSHOTS,
} from './constants';
import { ScreenshotLightbox } from './ScreenshotLightbox';

const KAIZEN_ICONS = [FolderKanban, ClipboardCheck, CalendarDays, BarChart3, ClipboardList, Truck, ShieldCheck, Sparkles];
const BMI_ICONS = [FileCheck2, Sparkles, QrCode, ShieldCheck];
const FLOWCREW_ICONS = [Brain, CalendarDays, BarChart3, Globe2];

const hasBbmiDomain = () => Boolean(BBMI_APP_DOMAIN && BBMI_APP_DOMAIN.trim());
const bbmiUrl = (path = '') => `https://${BBMI_APP_DOMAIN}${path}`;
const hasBbmiScreenshots = () => Array.isArray(BBMI_SCREENSHOTS) && BBMI_SCREENSHOTS.length > 0;
const hasFlowcrewDomain = () => Boolean(FLOWCREW_APP_DOMAIN && FLOWCREW_APP_DOMAIN.trim());
const flowcrewUrl = (path = '') => `https://${FLOWCREW_APP_DOMAIN}${path}`;

// Defensive image error handler — when an external screenshot URL
// disappears (e.g. a sister app moves its API), the broken-image icon
// is uglier than a clean hide. This nukes the whole <figure>
// wrapper so no caption/glow remains. Local /marketing assets should
// never trip this, but keeping the guard means a regression to remote
// URLs will degrade gracefully instead of leaving placeholders.
const hideOnError = (e) => {
  const fig = e.currentTarget.closest('figure');
  if (fig) fig.style.display = 'none';
};

const PRODUCT_PILLS = [
  { id: 'kaizen-kanri', label: 'Lean/CI', accent: '#00CED1' },
  { id: 'bbmi-oai',     label: 'BBMI-OAI', accent: '#00CED1' },
  { id: 'flowcrew',     label: 'FlowCrew', accent: FLOWCREW_ACCENT },
];

const ProductSubNav = () => {
  const [activeId, setActiveId] = useState(PRODUCT_PILLS[0].id);

  useEffect(() => {
    const targets = PRODUCT_PILLS
      .map(p => document.getElementById(p.id))
      .filter(Boolean);
    if (!targets.length || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the article whose top is closest to (but below) the
        // sticky sub-nav line. Sorting by boundingClientRect.top keeps
        // the highlight stable while scrolling.
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      // Trigger when an article crosses ~30% from the top — gives the
      // pill a natural lead-in moment before the heading fully arrives.
      { rootMargin: '-30% 0px -55% 0px', threshold: 0 }
    );
    targets.forEach(t => observer.observe(t));
    return () => observer.disconnect();
  }, []);

  const jumpTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      className="sticky top-16 z-30 -mx-4 sm:mx-0 mb-10 backdrop-blur-md bg-[#0a0a0a]/85 border-y border-white/[0.06]"
      data-testid="product-sub-nav"
    >
      <div className="flex items-center justify-center gap-1.5 px-4 py-2 overflow-x-auto">
        {PRODUCT_PILLS.map((p) => {
          const isActive = activeId === p.id;
          return (
            <a
              key={p.id}
              href={`#${p.id}`}
              onClick={jumpTo(p.id)}
              className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap"
              style={isActive ? {
                backgroundColor: `${p.accent}20`,
                borderColor: `${p.accent}50`,
                color: p.accent,
              } : {
                backgroundColor: 'transparent',
                borderColor: 'rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.50)',
              }}
              data-testid={`product-pill-${p.id}`}
              aria-current={isActive ? 'true' : undefined}
            >
              {p.label}
            </a>
          );
        })}
      </div>
    </div>
  );
};

// BBMI feature-illustration grid — pure CSS "screen mockups" for each
// of the four BBMI module pills. Built in JSX so the marketing
// section never breaks when sister-app endpoints disappear (root cause
// of the AWOL-screenshots regression). Replace with real screenshots
// the moment local files land in /public/marketing/bbmi/.
const BbmiFeatureGrid = ({ lang, bmi }) => {
  const cells = [
    {
      icon: FileCheck2,
      titleEn: 'NEN 2654-1',  titleNl: 'NEN 2654-1',
      subEn: 'Inspections',   subNl: 'Inspecties',
      visual: (
        <div className="space-y-1.5 mt-2 w-full">
          {[true, true, false].map((checked, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-sm flex-shrink-0 ${checked ? 'bg-[#00CED1]' : 'border border-white/30'}`} />
              <span className="h-1.5 rounded-full bg-white/15" style={{ width: `${60 - i * 10}%` }} />
            </div>
          ))}
        </div>
      ),
    },
    {
      icon: Sparkles,
      titleEn: 'Assisted fault analysis', titleNl: 'Ondersteunde storingsanalyse',
      subEn: 'Repeat-incident insight', subNl: 'Patroon-detectie',
      visual: (
        <div className="flex items-end gap-1 h-10 mt-3 w-full">
          {[40, 65, 55, 80, 35, 50, 25].map((h, i) => (
            <span
              key={i}
              className="flex-1 rounded-t-sm"
              style={{ height: `${h}%`, background: i === 3 ? '#00CED1' : 'rgba(0,206,209,0.35)' }}
            />
          ))}
        </div>
      ),
    },
    {
      icon: QrCode,
      titleEn: 'QR for fire dept',  titleNl: 'QR voor brandweer',
      subEn: 'Public inspection',   subNl: 'Publieke inspectie',
      visual: (
        <div className="mt-2 grid grid-cols-6 gap-[2px] w-14 h-14">
          {Array.from({ length: 36 }).map((_, i) => {
            const filled = [0,1,2,3,4,5,6,11,12,17,18,23,24,29,30,31,32,33,34,35,7,28,9,14,15,20,21,26].includes(i);
            return <span key={i} className={filled ? 'bg-white/85' : 'bg-transparent'} />;
          })}
        </div>
      ),
    },
    {
      icon: ShieldCheck,
      titleEn: 'Compliance dashboard',  titleNl: 'Compliance-dashboard',
      subEn: 'Real-time status',        subNl: 'Realtime status',
      visual: (
        <div className="mt-2 flex items-center gap-3">
          <div className="relative h-12 w-12">
            <div className="absolute inset-0 rounded-full border-2 border-white/10" />
            <div
              className="absolute inset-0 rounded-full border-2 border-[#00CED1]"
              style={{ clipPath: 'polygon(50% 0, 100% 0, 100% 100%, 50% 100%, 50% 50%, 0 50%, 0 0)' }}
            />
          </div>
          <div>
            <p className="text-base font-bold text-[#00CED1] leading-none">98%</p>
            <p className="text-[8px] text-white/40 mt-0.5">{lang === 'nl' ? 'op orde' : 'on track'}</p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3" data-testid="bbmi-feature-grid">
      {cells.map((c, i) => {
        const Icon = c.icon;
        const title = lang === 'nl' ? c.titleNl : c.titleEn;
        const sub = lang === 'nl' ? c.subNl : c.subEn;
        return (
          <div
            key={i}
            className="relative aspect-[4/3] p-3 rounded-xl border border-[#00CED1]/15 bg-gradient-to-br from-[#0f1e1e] via-[#0a1414] to-[#0a0a0a] overflow-hidden"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(0,206,209,0.10),transparent_55%)] pointer-events-none" />
            <div className="relative flex flex-col h-full">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Icon className="h-3 w-3 text-[#00CED1]" />
                <span className="text-[10px] font-semibold text-white">{title}</span>
              </div>
              <span className="text-[8px] uppercase tracking-wider text-white/30">{sub}</span>
              <div className="flex-1 flex items-end">
                {c.visual}
              </div>
            </div>
          </div>
        );
      })}
      <p className="col-span-2 mt-1 text-[10px] text-white/35 text-center italic">
        {bmi?.tileSub || ''}
      </p>
    </div>
  );
};



// `only` renders a single product so the landing page can lead with
// Kanriva™, place FlowCrew right behind it as its mobile companion, and
// park the sibling app further down under "Also built by Kaizen Kanri".
export const PlatformSection = ({ only = null }) => {
  const { t, lang } = useLang('products');
  const [zoom, setZoom] = useState(null);
  const demoNote = lang === 'nl' ? DEMO_DATA_NOTE_NL : DEMO_DATA_NOTE_EN;
  const kaizen = t.kaizen || {};
  const bmi = t.bmi || {};
  const flowcrew = t.flowcrew || {};
  const cta = t.cta || {};
  const show = (key) => !only || only === key;
  const header = (only === 'flowcrew' ? t.flowcrewHeader
    : only === 'bbmi' ? t.alsoHeader
      : only === 'kaizen' ? t.kanrivaHeader
        : null) || { eyebrow: t.eyebrow, title: t.title, subtitle: t.subtitle };
  const sectionId = only === 'flowcrew' ? 'flowcrew-section'
    : only === 'bbmi' ? 'also-built' : 'platform';

  return (
    <section id={sectionId} className="py-28 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a] relative">
      {/* Decorative radial gradient — wrapped in its own overflow-hidden
          container so the section itself can host `position: sticky`
          children (an `overflow:hidden` ancestor breaks sticky). */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-24 w-[520px] h-[520px] bg-[#00CED1]/8 rounded-full blur-[160px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-14">
          {only === 'kaizen' ? (
            <img
              src="/brand/kanriva-mark.png"
              alt="Kanriva™"
              className="h-16 sm:h-20 w-auto mx-auto mb-5"
              data-testid="platform-header-mark"
            />
          ) : (
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="h-1 w-8 bg-[#00CED1] rounded-full" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00CED1]">
                {header.eyebrow}
              </span>
              <span className="h-1 w-8 bg-[#00CED1] rounded-full" />
            </div>
          )}
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
            {header.title}
          </h2>
          <p className="text-lg text-white/55 max-w-3xl mx-auto">
            {header.subtitle}
          </p>
        </div>

        {!only && <ProductSubNav />}

        {/* PRODUCT 1: Kanriva™ (Lean / CI flagship) */}
        {show('kaizen') && (
        <article
          id="kaizen-kanri"
          className="mb-20 p-6 sm:p-10 rounded-3xl bg-white/[0.02] border border-white/[0.06] scroll-mt-28"
          data-testid="product-card-kaizen-kanri"
        >
          {/* Symmetric composition: centred header, two equal text
              columns, a 4-up module row, a 4-up screenshot row and a
              centred CTA — so both halves always carry the same weight. */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00CED1]/10 border border-[#00CED1]/25 text-[#00CED1] text-xs font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00CED1] animate-pulse" /> {t.liveBadge}
            </div>
            {kaizen.tagline && (
              <p className="mt-5 text-lg md:text-xl font-semibold text-[#00CED1] max-w-3xl mx-auto" data-testid="kaizen-tagline">
                {kaizen.tagline}
              </p>
            )}
          </div>

          <p className="mt-6 text-white/55 leading-relaxed text-center max-w-4xl mx-auto">
            {kaizen.description}
          </p>

          <ul className="mt-10 grid md:grid-cols-2 gap-x-12 gap-y-3" data-testid="kaizen-bullets">
            {(kaizen.bullets || []).map((b, i, arr) => (
              <li
                key={b}
                className={`flex gap-2.5 text-sm text-white/65 leading-relaxed ${
                  arr.length % 2 === 1 && i === arr.length - 1 ? 'md:col-span-2 md:justify-center md:text-center md:max-w-3xl md:mx-auto' : ''
                }`}
              >
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#00CED1] flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-2.5">
            {(kaizen.modules || []).map((label, i) => {
              const Icon = KAIZEN_ICONS[i] || FolderKanban;
              return (
                <div
                  key={label}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.05] transition-colors hover:border-[#00CED1]/30 hover:bg-white/[0.05]"
                >
                  <Icon className="h-4 w-4 text-[#00CED1] flex-shrink-0" />
                  <span className="text-xs text-white/70 leading-snug">{label}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kanriva-screenshot-grid">
            {KANRIVA_SCREENSHOTS.map((s, i) => {
              const caption = lang === 'nl' ? s.labelNl : s.labelEn;
              return (
                <figure key={s.src} className="m-0 relative group">
                  <div className="absolute -inset-2 bg-gradient-to-br from-[#00CED1]/15 to-transparent rounded-2xl blur-2xl pointer-events-none" />
                  <button
                    type="button"
                    onClick={() => setZoom({ src: s.src, caption })}
                    className="relative block w-full cursor-zoom-in rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00CED1]"
                    aria-label={`${caption} — ${demoNote}`}
                    data-testid={`kanriva-screenshot-zoom-${i}`}
                  >
                    <img
                      src={s.src}
                      alt={`Kanriva™ — ${caption} (demo data)`}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      onError={hideOnError}
                      className="relative w-full rounded-xl border border-white/10 shadow-2xl transition-transform duration-500 group-hover:-translate-y-1"
                      data-testid={`kanriva-screenshot-${i}`}
                    />
                    <span className="pointer-events-none absolute right-2 top-2 h-7 w-7 rounded-md bg-black/60 border border-white/20 text-white/80 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                      <Maximize2 className="h-3.5 w-3.5" />
                    </span>
                  </button>
                  <figcaption className="mt-2 text-[11px] text-white/30 text-center">{caption}</figcaption>
                </figure>
              );
            })}
          </div>

          <div className="mt-10 flex justify-center">
            <Button
              size="lg"
              className="bg-[#00CED1] hover:bg-[#00b4b7] text-[#0a0a0a] font-semibold"
              asChild
              data-testid="product-cta-kaizen-login"
            >
              <Link to="/login">
                <LogIn className="mr-2 h-4 w-4" /> {kaizen.cta}
              </Link>
            </Button>
          </div>
        </article>
        )}

        {/* ALSO BUILT: BBMI-OAI digital logbook */}
        {show('bbmi') && (
        <article
          id="bbmi-oai"
          className="mb-16 p-6 sm:p-10 rounded-3xl bg-white/[0.02] border border-white/[0.06] scroll-mt-28"
          data-testid="product-card-bmi-logboek"
        >
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-2 order-2 lg:order-1">
              {hasBbmiScreenshots() ? (
                // Real BBMI screenshots — phone-frame style matches
                // the FlowCrew grid for visual parity.
                <div className="grid grid-cols-2 gap-3" data-testid="bbmi-screenshot-grid">
                  {BBMI_SCREENSHOTS.map((s, i) => {
                    const caption = lang === 'nl' ? s.labelNl : s.labelEn;
                    return (
                      <figure key={s.src} className="m-0 text-center group">
                        <div className="relative">
                          <div className="absolute -inset-1.5 bg-gradient-to-br from-[#00CED1]/20 to-transparent rounded-2xl blur-lg pointer-events-none" />
                          <button
                            type="button"
                            onClick={() => setZoom({ src: s.src, caption })}
                            className="relative block w-full cursor-zoom-in rounded-[20px] overflow-hidden border border-[#00CED1]/30 bg-[#0a0a0a] shadow-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00CED1]"
                            style={{ aspectRatio: '9 / 19.5' }}
                            aria-label={`${caption} — ${demoNote}`}
                            data-testid={`bbmi-screenshot-zoom-${i}`}
                          >
                            <img
                              src={s.src}
                              alt={caption}
                              loading="lazy"
                              onError={hideOnError}
                              className="w-full h-full object-cover object-top"
                              data-testid={`bbmi-screenshot-${i}`}
                            />
                            <span className="pointer-events-none absolute right-1.5 top-1.5 h-6 w-6 rounded-md bg-black/60 border border-white/20 text-white/80 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                              <Maximize2 className="h-3 w-3" />
                            </span>
                          </button>
                        </div>
                        <figcaption className="mt-1.5 text-[10px] text-white/40">
                          {caption}
                        </figcaption>
                      </figure>
                    );
                  })}
                </div>
              ) : (
                // Feature-illustration grid — 2×2 mini "screen" tiles
                // hand-drawn in JSX so they can never 404. Each tile
                // visualises one of the four BBMI module pills, with a
                // tasteful brand-accent background and a one-line label.
                // Replace with real screenshots by populating
                // BBMI_SCREENSHOTS with local /marketing/bbmi/ paths.
                <BbmiFeatureGrid lang={lang} bmi={bmi} />
              )}
            </div>

            <div className="lg:col-span-3 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00CED1]/10 border border-[#00CED1]/25 text-[#00CED1] text-xs font-semibold mb-4">
                <span className="h-1.5 w-1.5 rounded-full bg-[#00CED1] animate-pulse" /> {t.liveBadge}
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                {bmi.title}
              </h3>
              <p className="text-white/55 leading-relaxed mb-4">
                {bmi.description}
              </p>
              <ul className="space-y-1.5 text-sm text-white/55 mb-6">
                {(bmi.bullets || []).map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1 w-1 rounded-full bg-[#00CED1]" /> {b}
                  </li>
                ))}
              </ul>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                {(bmi.modules || []).map((label, i) => {
                  const Icon = BMI_ICONS[i] || FileCheck2;
                  return (
                    <div
                      key={label}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.05]"
                    >
                      <Icon className="h-4 w-4 text-[#00CED1] flex-shrink-0" />
                      <span className="text-xs text-white/70">{label}</span>
                    </div>
                  );
                })}
              </div>

              {/* CTA cluster — Play badge appears when the listing is
                  live. The "Open the app" CTA was intentionally
                  removed per request: visitors discover the app via
                  the Play Store, not a direct web link. */}
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  className="border-white/15 text-white hover:bg-white/5"
                  asChild
                  data-testid="product-cta-bmi-contact"
                >
                  <a href="#contact">
                    {bmi.ctaContact} <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                {BBMI_PLAY_STORE_URL ? (
                  <a
                    href={BBMI_PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/[0.04] border border-white/15 text-white text-xs font-semibold hover:bg-white/[0.08] transition-colors"
                    data-testid="product-cta-bmi-play"
                  >
                    <img
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt={bmi.ctaPlay}
                      className="h-7 w-auto"
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <span
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-dashed border-white/15 text-white/40 text-[11px]"
                    data-testid="product-cta-bmi-play-pending"
                    title="Google Play listing pending approval"
                  >
                    {bmi.ctaPlayPending}
                  </span>
                )}
                <span className="text-[11px] text-white/35" data-testid="product-ios-note-bmi">
                  {bmi.iosNote}
                </span>
              </div>
            </div>
          </div>
        </article>
        )}

        {/* FLOWCREW — the mobile companion. Brand accent: amber #FFB800. */}
        {show('flowcrew') && (
        <article
          id="flowcrew"
          className="mb-16 p-6 sm:p-10 rounded-3xl bg-white/[0.02] border border-white/[0.06] scroll-mt-28"
          style={{ boxShadow: `inset 0 0 0 1px ${FLOWCREW_ACCENT}10` }}
          data-testid="product-card-flowcrew"
        >
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* Phone-mockup screenshot strip — 4 portrait phone tiles
                in a responsive row. Aspect ~9:19.5 to feel native. */}
            <div className="lg:col-span-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="flowcrew-screenshot-grid">
                {FLOWCREW_SCREENSHOTS.map((s, i) => {
                  const caption = lang === 'nl' ? s.labelNl : s.labelEn;
                  return (
                    <figure key={s.src} className="m-0 text-center group">
                      <div className="relative">
                        <div
                          className="absolute -inset-1.5 rounded-2xl blur-lg pointer-events-none"
                          style={{ background: `linear-gradient(135deg, ${FLOWCREW_ACCENT}25, transparent)` }}
                        />
                        <button
                          type="button"
                          onClick={() => setZoom({ src: s.src, caption })}
                          className="relative block w-full cursor-zoom-in rounded-[20px] overflow-hidden border bg-[#0a0a0a] focus:outline-none focus-visible:ring-2"
                          style={{ aspectRatio: '9 / 19.5', borderColor: `${FLOWCREW_ACCENT}30` }}
                          aria-label={`${caption} — ${demoNote}`}
                          data-testid={`flowcrew-screenshot-zoom-${i}`}
                        >
                          <img
                            src={s.src}
                            alt={caption}
                            loading="lazy"
                            onError={hideOnError}
                            className="w-full h-full object-cover"
                            data-testid={`flowcrew-screenshot-${i}`}
                          />
                          <span className="pointer-events-none absolute right-1.5 top-1.5 h-6 w-6 rounded-md bg-black/60 border border-white/20 text-white/80 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                            <Maximize2 className="h-3 w-3" />
                          </span>
                        </button>
                      </div>
                      <figcaption className="mt-1.5 text-[10px] text-white/40">{caption}</figcaption>
                    </figure>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-2">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 border"
                style={{
                  backgroundColor: `${FLOWCREW_ACCENT}15`,
                  borderColor: `${FLOWCREW_ACCENT}40`,
                  color: FLOWCREW_ACCENT,
                }}
              >
                <Smartphone className="h-3.5 w-3.5" />
                {flowcrew.eyebrow}
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                {flowcrew.title}
              </h3>
              <p className="text-white/55 leading-relaxed mb-4">
                {flowcrew.description}
              </p>
              <ul className="space-y-1.5 text-sm text-white/55 mb-5">
                {(flowcrew.bullets || []).map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <span
                      className="mt-1.5 h-1 w-1 rounded-full flex-shrink-0"
                      style={{ backgroundColor: FLOWCREW_ACCENT }}
                    />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                {(flowcrew.modules || []).map((label, i) => {
                  const Icon = FLOWCREW_ICONS[i] || UsersIcon;
                  return (
                    <div
                      key={label}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.05]"
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" style={{ color: FLOWCREW_ACCENT }} />
                      <span className="text-xs text-white/70">{label}</span>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  className="border-white/15 text-white hover:bg-white/5"
                  asChild
                  data-testid="product-cta-flowcrew-contact"
                >
                  <a href="#contact">
                    {flowcrew.ctaContact} <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                {FLOWCREW_PLAY_STORE_LIVE && FLOWCREW_PLAY_STORE_URL ? (
                  <a
                    href={FLOWCREW_PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center"
                    data-testid="product-cta-flowcrew-play"
                  >
                    <img
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt={flowcrew.ctaPlay}
                      className="h-10 w-auto"
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <span
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-dashed border-white/15 text-white/40 text-[11px]"
                    data-testid="product-cta-flowcrew-play-pending"
                    title="Pending Google Play approval"
                  >
                    {flowcrew.ctaPlayPending}
                  </span>
                )}
                <span className="text-[11px] text-white/35" data-testid="product-ios-note-flowcrew">
                  {flowcrew.iosNote}
                </span>
              </div>

              <p className="mt-4 text-[11px] text-white/30 italic">
                {flowcrew.attribution}
              </p>
            </div>
          </div>
        </article>
        )}

        {/* TAILORED-BUILD CTA STRIP */}
        {(!only || only === 'bbmi') && (
        <div className="rounded-3xl bg-gradient-to-br from-[#00CED1]/10 via-[#0a0a0a] to-[#0a0a0a] border border-[#00CED1]/20 p-8 sm:p-10 text-center">
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            {cta.title}
          </h3>
          <p className="text-white/55 max-w-2xl mx-auto mb-6">
            {cta.sub}
          </p>
          <Button
            size="lg"
            className="h-14 px-8 text-base bg-[#00CED1] hover:bg-[#00b4b7] text-[#0a0a0a] font-semibold"
            asChild
            data-testid="platform-cta-tailored-build"
          >
            <a href="#contact">
              {cta.button} <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </Button>
        </div>
        )}
      </div>

      <ScreenshotLightbox
        shot={zoom?.src}
        caption={zoom?.caption}
        note={demoNote}
        onClose={() => setZoom(null)}
      />
    </section>
  );
};
